// Tela do Marketing na Visão Geral (#6504): Maycon. Abre no mês, com seletor.
//
// Pergunta da tela: a mídia está trazendo contato barato que vira contrato?
//   - hoje: a mídia de ontem por campanha (gasto, contatos, custo por contato) e as minhas
//     entregas da Central (vencendo e atrasadas);
//   - mês: custo por contato (meta R$ 8,50), qualidade do lead (meta 10%, #6507), contratos
//     de mídia no trimestre (meta 25) e o investimento; detalhe: custo por campanha no mês e
//     os melhores criativos.
// A verba aprovada fica na tarefa 17.
import React, { useMemo } from 'react';
import { BarChart2, MessagesSquare, Sparkles } from 'lucide-react';
import { META_CUSTO_POR_CONTATO } from '../../../src/lib/visaoGeral/tecnologia';
import { BlocosDoDia } from '../BlocosDoDia';
import { CartoesMidiaDoMes, PainelCampanhas, PainelMelhoresCriativos, useMidiaDeOntem, useMidiaDoMes } from '../blocosTecnologiaMarketing';
import { CartaoQualidadeDoLead } from '../blocosComercialFase2';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, ListaAtalhos, SecaoVisaoGeral, plural } from '../componentes';
import { useMinhasTarefas } from '../useMinhasTarefas';
import { soSolicitacoes } from '../../../src/lib/visaoGeral/minhasTarefas';
import { MinhaFila } from './TelaPadrao';
import type { PropsTelaVisaoGeral } from '../tipos';

const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const reais0 = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const dataCurta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

const TelaMarketing: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo, aoMudarMes }) => {
  const { dados: todas } = useMinhasTarefas(pessoa.id);
  // Só as solicitações da Central: atividade do CRM (visita, ligação) não é demanda.
  const minhas = useMemo(() => (todas ? soSolicitacoes(todas) : null), [todas]);
  const ontem = useMidiaDeOntem();
  const m = useMidiaDoMes(periodo.mes);
  const total = ontem.total;

  const acoes = souEu ? <BotaoAcao rotulo="Campanhas" icone={BarChart2} para="/marketing/campanhas" /> : null;
  const totalMes = useMemo(() => m.atual, [m.atual]);

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} aoMudarMes={aoMudarMes} acoes={acoes} />

      <BlocosDoDia pessoa={pessoa} perfil="marketing" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo={`mídia de ontem (${dataCurta(ontem.ontem)}) e minhas entregas`}>
        <CartaoHoje valor={total ? total.contatos : null} rotulo="contatos ontem" tom="neutro" para="/marketing/campanhas"
          dica={total ? `${total.leads} leads de formulário e ${total.conversas} conversas no WhatsApp, com ${reais0(total.gasto)} investidos.` : undefined} />
        <CartaoHoje valor={total ? (total.custo == null ? '—' : reais(total.custo)) : null} rotulo="custo por contato ontem"
          tom={total?.custo == null ? 'neutro' : total.custo <= META_CUSTO_POR_CONTATO ? 'ok' : 'atencao'} para="/marketing/campanhas"
          dica={`Meta do mês: até ${reais(META_CUSTO_POR_CONTATO)} por contato.`} />
        <CartaoHoje valor={minhas ? minhas.atrasadas.length : null} rotulo="entregas atrasadas" tom={minhas && minhas.atrasadas.length ? 'critico' : 'ok'} para="/tarefas/solicitacoes" />
        <CartaoHoje valor={minhas ? minhas.hoje.length : null} rotulo="entregas vencem hoje" tom="atencao" para="/tarefas/solicitacoes"
          dica={minhas ? `Mais ${plural(minhas.proximas.length, 'entrega', 'entregas')} nos próximos 7 dias.` : undefined} />
      </SecaoVisaoGeral>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PainelCampanhas titulo={`Mídia de ontem (${dataCurta(ontem.ontem)})`} total={total} campanhas={ontem.campanhas}
          vazio="Sem gasto em mídia importado para ontem. A importação do Meta roda uma vez por dia." />
        <MinhaFila dados={minhas} titulo="Minhas entregas" />
      </div>

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={`${m.j.rotulo}, Meta Ads e contratos`}>
        <CartoesMidiaDoMes m={m} qualidade={<CartaoQualidadeDoLead mes={periodo.mes} corrente={periodo.corrente} />} />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PainelCampanhas titulo={`Custo por contato por campanha em ${m.j.rotulo}`} total={totalMes} campanhas={m.campanhasDoMes}
            vazio={`Sem gasto em mídia em ${m.j.rotulo}.`} />
          <PainelMelhoresCriativos criativos={m.criativos} rotuloMes={m.j.rotulo} />
        </div>
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Campanhas', para: '/marketing/campanhas', icone: BarChart2 },
        { rotulo: 'Criativos', para: '/marketing/criativos', icone: Sparkles },
        { rotulo: 'Análise das conversas', para: '/crm/analise-conversas', icone: MessagesSquare },
      ]} />
    </div>
  );
};

export default TelaMarketing;
